import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import MenuItem from '@rio-cloud/rio-uikit/MenuItem';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DriverStatus } from '../../../../../types';
import type {
    DriversTableFilterPropertiesFromDispatch,
    DriversTableFilterOwnProps,
    DriversTableFilterPropertiesFromState,
} from '../containers/DriversTableFilterContainer';

export type DriversTableFilterProps = DriversTableFilterPropertiesFromState &
    DriversTableFilterPropertiesFromDispatch &
    DriversTableFilterOwnProps;

export const DriversTableFilter = (props: DriversTableFilterProps) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);

    const activeDriversChecked = props.driverStatuses.includes(DriverStatus.ACTIVE);
    const archivedDriversChecked = props.driverStatuses.includes(DriverStatus.ARCHIVED);
    const hasActiveDisplayOptions =
        activeDriversChecked || archivedDriversChecked || props.gridLines || props.compactRows;

    const updateDriverStatuses = (nextState: { activeDriversChecked?: boolean; archivedDriversChecked?: boolean }) => {
        const nextActiveDriversChecked = nextState.activeDriversChecked ?? activeDriversChecked;
        const nextArchivedDriversChecked = nextState.archivedDriversChecked ?? archivedDriversChecked;
        const driverStatusesUpdate: DriverStatus[] = [];

        if (nextActiveDriversChecked) {
            driverStatusesUpdate.push(DriverStatus.ACTIVE);
        }

        if (nextArchivedDriversChecked) {
            driverStatusesUpdate.push(DriverStatus.ARCHIVED);
        }

        props.changeDriverStatuses(driverStatusesUpdate);
    };

    return (
        <ButtonDropdown
            aria-label={intl.formatMessage({
                id: 'intl-msg:common-message.table.filter.options',
                defaultMessage: 'Display options',
            })}
            title=''
            iconName={hasActiveDisplayOptions ? 'icon-filter-active' : 'icon-filter'}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            pullRight
            iconOnly
            noCaret
            data-testid='tableFilterOptionsButton'
            dropdownClassName='width-250 padding-5'
            customDropdown={
                <>
                    <MenuItem
                        header
                        value={
                            <FormattedMessage
                                id='intl-msg:common-message.table.filter.options'
                                defaultMessage='Display options'
                            />
                        }
                    />
                    <div className='padding-x-10 padding-bottom-15' data-testid='driversTableFilterOptions'>
                        <div>
                            <div className='text-bold text-color-darkest margin-bottom-10'>
                                <FormattedMessage id='intl-msg:status' />
                            </div>
                            <div className='space-y-5'>
                                <Checkbox
                                    data-testid='drivers-table-filter-active'
                                    checked={activeDriversChecked}
                                    label={<FormattedMessage id='intl-msg:status.active' />}
                                    onChange={() =>
                                        updateDriverStatuses({
                                            activeDriversChecked: !activeDriversChecked,
                                        })
                                    }
                                />
                                <Checkbox
                                    data-testid='drivers-table-filter-archived'
                                    checked={archivedDriversChecked}
                                    label={<FormattedMessage id='intl-msg:status.archived' />}
                                    onChange={() =>
                                        updateDriverStatuses({
                                            archivedDriversChecked: !archivedDriversChecked,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className='margin-top-20'>
                            <div className='text-bold text-color-darkest margin-bottom-10'>
                                <FormattedMessage
                                    id='intl-msg:common-message.table.displayOptions.table'
                                    defaultMessage='Table'
                                />
                            </div>
                            <div className='space-y-5'>
                                <Checkbox
                                    checked={props.gridLines}
                                    label={
                                        <FormattedMessage
                                            id='intl-msg:common-message.table.displayOptions.gridLines'
                                            defaultMessage='Grid lines'
                                        />
                                    }
                                    onChange={props.onToggleGridLines}
                                />
                                <Checkbox
                                    checked={props.compactRows}
                                    label={
                                        <FormattedMessage
                                            id='intl-msg:common-message.table.displayOptions.condensed'
                                            defaultMessage='Compact rows'
                                        />
                                    }
                                    onChange={props.onToggleCompactRows}
                                />
                            </div>
                        </div>
                    </div>
                </>
            }
        />
    );
};
